import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import {
  fetchLoginHistory,
  LOGIN_HISTORY_PAGE_SIZE,
  type LoginHistoryFilters,
} from "../../../api/loginHistory";
import {
  ENUM_DEVICE_CATEGORY,
  ENUM_LOGIN_STATUS,
  enumdevicecategoryToI18nKey,
  enumloginstatusToI18nKey,
  type EnumDeviceCategory,
  type EnumLoginStatus,
} from "../../../generated/enums";
import type { LoginHistory } from "../../../generated/types";
import { sectionToIcon } from "../../type/default";
import { LoginStatusBadge } from "./LoginStatusBadge";
import {
  ActiveFilterChips,
  type ActiveFilterChip,
} from "../../common/ActiveFilterChips";
import {
  DataTable,
  DataTableColumn,
  DataTableRow,
  DataTableSkeletonRows,
} from "../../common/DataTable";
import { EmptyState } from "../../common/EmptyState";
import {
  FILTER_BUTTON_PRIMARY,
  FILTER_BUTTON_SECONDARY,
  FILTER_CONTROL,
  FILTER_LABEL,
  FilterSelect,
} from "../../common/FilterControls";
import { EntityCard, EntityCardSkeletons } from "../../common/EntityCard";
import { FilterableValue } from "../../common/FilterableValue";
import { Pagination } from "../../common/Pagination";
import { HistorySearchInput } from "../../common/HistorySearchInput";

const EMPTY_FILTERS: LoginHistoryFilters = {
  q: "",
  email: "",
  status: "",
  ip: "",
  deviceType: "",
  idAccount: undefined,
  browser: "",
  os: "",
  dateFrom: "",
  dateTo: "",
};

/** Date + heure : sans l'heure, on ne distingue pas deux tentatives du même jour. */
const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
};

/** Repli « — » plutôt qu'une chaîne vide : une cellule vide se lit comme un bug. */
const orDash = (value: string) => (value.trim() === "" ? "-" : value);

/**
 * Onglet « Connexions utilisateurs » de l'écran Historique (admin/PSC) :
 * qui s'est connecté au site, quand, d'où et avec quoi.
 *
 * Même famille que RemoteConnectionsTable (recherche + pagination serveur + statut
 * coloré), mais les DONNÉES diffèrent : ici pas de produit ni de canal, et en revanche
 * l'IP, le navigateur et le type d'appareil — ce qui permet de reconnaître une tentative
 * anormale.
 *
 * Pas de `Header` : il est rendu une seule fois par HistoryPage, au-dessus des onglets.
 */
export const LoginHistoryTable = () => {
  const { t } = useTranslation();

  // `applied` = ce qui part au serveur ; `draft` = ce que l'utilisateur saisit.
  // Les séparer évite une requête à chaque frappe dans le champ email.
  const [draft, setDraft] = useState<LoginHistoryFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<LoginHistoryFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<LoginHistory[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Filtres repliés par défaut sous `md` : six champs empilés pousseraient les
  // résultats hors écran au chargement.
  const [filtersOpen, setFiltersOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { page: result, error: err } = await fetchLoginHistory(applied, page);
    setRows(result.rows);
    setTotal(result.total);
    setError(err);
    setLoading(false);
  }, [applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  const activeFilterCount = useMemo(
    () =>
      Object.values(applied).filter((v) => v !== "" && v !== undefined).length,
    [applied]
  );

  const submitFilters = () => {
    setPage(0); // un nouveau filtre repart de la première page, sinon on tombe hors bornes
    setApplied(draft);
  };

  /**
   * Filtre posé par un CLIC sur une valeur du tableau.
   *
   * Applique tout de suite, sans passer par « Rechercher » : le clic EST la validation.
   * `draft` est synchronisé en même temps, sinon la barre de filtres afficherait encore
   * l'état précédent et l'écraserait au prochain « Rechercher ».
   */
  const filterBy = (patch: Partial<LoginHistoryFilters>) => {
    setPage(0);
    setDraft((prev) => ({ ...prev, ...patch }));
    setApplied((prev) => ({ ...prev, ...patch }));
  };

  /** Retire un critère (croix d'une puce). */
  const clearFilter = (patch: Partial<LoginHistoryFilters>) => filterBy(patch);

  /**
   * Un clic sur une date filtre le JOUR ENTIER : la colonne stocke un horodatage à la
   * seconde, mais on voit « 24/09 » — filtrer sur 14:30:07 ne renverrait qu'une ligne.
   */
  const filterByDay = (iso: string) => {
    const day = iso.slice(0, 10);
    filterBy({ dateFrom: day, dateTo: day });
  };

  /**
   * Maj+clic : étend la plage jusqu'à la date cliquée, en gardant la borne la plus
   * ancienne déjà posée. Le clic SIMPLE, lui, remplace toujours — sinon le geste
   * deviendrait imprévisible (« pourquoi ai-je deux jours ? »).
   */
  const extendToDay = (iso: string) => {
    const day = iso.slice(0, 10);
    const start = applied.dateFrom || day;
    const [from, to] = day < start ? [day, start] : [start, day];
    filterBy({ dateFrom: from, dateTo: to });
  };

  /** Puces des filtres actifs. Chacune affiche le LIBELLÉ, jamais l'id envoyé au serveur. */
  const chips: ActiveFilterChip[] = [];
  if (applied.idAccount) {
    // L'email de la première ligne correspondante sert de libellé : on filtre par id,
    // mais « 7 » ne veut rien dire pour un lecteur.
    const label = rows.find((r) => r.id_account === applied.idAccount)?.email;
    chips.push({
      key: "id_account",
      field: t("history-account"),
      value: label || `#${applied.idAccount}`,
      onRemove: () => clearFilter({ idAccount: undefined }),
    });
  }
  if (applied.q) {
    chips.push({
      key: "q",
      field: t("filter-search"),
      value: applied.q,
      onRemove: () => clearFilter({ q: "" }),
    });
  }
  if (applied.ip) {
    chips.push({
      key: "ip",
      field: t("history-ip"),
      value: applied.ip,
      onRemove: () => clearFilter({ ip: "" }),
    });
  }
  if (applied.status) {
    chips.push({
      key: "status",
      field: t("device-status"),
      value: String(t(enumloginstatusToI18nKey[applied.status])),
      onRemove: () => clearFilter({ status: "" }),
    });
  }
  if (applied.browser) {
    chips.push({
      key: "browser",
      field: t("history-browser"),
      value: applied.browser,
      onRemove: () => clearFilter({ browser: "" }),
    });
  }
  if (applied.os) {
    chips.push({
      key: "os",
      field: "OS",
      value: applied.os,
      onRemove: () => clearFilter({ os: "" }),
    });
  }
  if (applied.deviceType) {
    chips.push({
      key: "device_type",
      field: t("history-device-category"),
      value: String(t(enumdevicecategoryToI18nKey[applied.deviceType])),
      onRemove: () => clearFilter({ deviceType: "" }),
    });
  }
  if (applied.dateFrom || applied.dateTo) {
    // Un seul jour → « 24/09 » ; une plage → « 23/09 → 26/09 ». L'état doit se lire
    // d'un coup d'œil, sans avoir à ouvrir la barre de filtres.
    const short = (d?: string) =>
      d ? d.split("-").reverse().slice(0, 2).join("/") : "…";
    const value =
      applied.dateFrom === applied.dateTo
        ? short(applied.dateFrom)
        : `${short(applied.dateFrom)} → ${short(applied.dateTo)}`;
    chips.push({
      key: "date",
      field: t("history-date"),
      value,
      onRemove: () => clearFilter({ dateFrom: "", dateTo: "" }),
    });
  }

  const resetFilters = () => {
    setPage(0);
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const scrollKey = `${page}|${JSON.stringify(applied)}`;

  const columns: DataTableColumn<
    "date" | "email" | "ip" | "browser" | "category" | "status"
  >[] = [
    {
      key: "date",
      header: t("history-date"),
      className: "w-2/12 whitespace-nowrap",
    },
    { key: "email", header: t("history-email"), className: "w-3/12 break-all" },
    {
      key: "ip",
      header: t("history-ip"),
      className: "w-2/12 whitespace-nowrap",
    },
    { key: "browser", header: t("history-browser"), className: "w-2/12" },
    {
      key: "category",
      header: t("history-device-category"),
      className: "w-1/12",
    },
    { key: "status", header: t("device-status"), className: "w-2/12" },
  ];

  return (
    <div>
      {/* Barre de filtres — empilée en mobile, repliable sous `md`. */}
      <div className="mt-6">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          aria-expanded={filtersOpen}
          className="md:hidden flex items-center gap-2 text-default-text font-medium"
        >
          <FontAwesomeIcon icon="filter" />
          {t("filter-by")}
          {/* Sans ce compteur, un utilisateur oublie qu'il filtre et croit la liste vide. */}
          {activeFilterCount > 0 && (
            <span className="rounded-full bg-secondary px-2 text-xs text-white">
              {activeFilterCount}
            </span>
          )}
        </button>

        <div
          className={`${filtersOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row md:flex-wrap md:items-end gap-3 mt-3 md:mt-0`}
        >
          <HistorySearchInput
            label={t("filter-search")}
            value={draft.q ?? ""}
            onChange={(value) => setDraft({ ...draft, q: value })}
            onSubmit={submitFilters}
            // i18n.t("history-search-placeholder-login")
            placeholder={t("history-search-placeholder-login")}
          />

          <label className={FILTER_LABEL}>
            {t("device-status")}
            <FilterSelect
              value={draft.status ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  status: e.target.value as EnumLoginStatus | "",
                })
              }
            >
              <option value="">{t("history-all")}</option>
              {ENUM_LOGIN_STATUS.map((s) => (
                <option key={s} value={s}>
                  {t(enumloginstatusToI18nKey[s])}
                </option>
              ))}
            </FilterSelect>
          </label>

          <label className={FILTER_LABEL}>
            {t("history-device-category")}
            <FilterSelect
              value={draft.deviceType ?? ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  deviceType: e.target.value as EnumDeviceCategory | "",
                })
              }
            >
              <option value="">{t("history-all")}</option>
              {ENUM_DEVICE_CATEGORY.map((d) => (
                <option key={d} value={d}>
                  {t(enumdevicecategoryToI18nKey[d])}
                </option>
              ))}
            </FilterSelect>
          </label>

          <label className={FILTER_LABEL}>
            {t("history-ip")}
            <input
              type="text"
              value={draft.ip ?? ""}
              onChange={(e) => setDraft({ ...draft, ip: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && submitFilters()}
              className={FILTER_CONTROL}
            />
          </label>

          <label className={FILTER_LABEL}>
            {t("history-date-from")}
            <input
              type="date"
              value={draft.dateFrom ?? ""}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value })}
              className={FILTER_CONTROL}
            />
          </label>

          <label className={FILTER_LABEL}>
            {t("history-date-to")}
            <input
              type="date"
              value={draft.dateTo ?? ""}
              onChange={(e) => setDraft({ ...draft, dateTo: e.target.value })}
              className={FILTER_CONTROL}
            />
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={submitFilters}
              className={FILTER_BUTTON_PRIMARY}
            >
              {t("filter-search")}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className={FILTER_BUTTON_SECONDARY}
            >
              {t("history-reset")}
            </button>
          </div>
        </div>
      </div>

      {/* Les puces sont AU-DESSUS de la zone de résultats, et non dedans : un filtre
          trop restrictif donne « aucun résultat », et c'est précisément le moment où
          l'utilisateur a besoin de voir ce qui filtre — et de pouvoir le retirer. */}
      <ActiveFilterChips chips={chips} onClearAll={resetFilters} />

      {error ? (
        <p className="mt-10 text-center text-red-700">{t("error")}</p>
      ) : loading ? (
        <>
          <div className="md:hidden mt-6">
            <EntityCardSkeletons rows={4} />
          </div>
          <div className="hidden md:block mt-6">
            <DataTable columns={columns} scrollKey={scrollKey}>
              <DataTableSkeletonRows columns={columns} />
            </DataTable>
          </div>
        </>
      ) : rows.length === 0 ? (
        // Deux situations très différentes : rien à voir, ou un filtre trop restrictif.
        // Le même message pour les deux laisserait croire à un bug.
        <EmptyState
          icon={sectionToIcon.section_history}
          title={
            activeFilterCount > 0
              ? t("table-no-result")
              : t("history-empty-login")
          }
        />
      ) : (
        <>
          {/* MOBILE : une carte par tentative, au style des cartes Produits. Elles portent
              les MÊMES filtres que le tableau : ajouter un critère à l'un sans l'autre est
              l'oubli classique. Pas de Maj+clic au tactile, donc pas de plage de dates. */}
          <div className="md:hidden space-y-4 mt-6">
            {rows.map((row) => (
              <EntityCard
                key={row.id}
                badge={
                  <FilterableValue
                    variant="badge"
                    label={<LoginStatusBadge status={row.status} />}
                    active={applied.status === row.status}
                    onFilter={() => filterBy({ status: row.status })}
                    title={t("filter-title-status")}
                  />
                }
                title={
                  <FilterableValue
                    label={orDash(row.email)}
                    active={applied.idAccount === row.id_account}
                    onFilter={() => filterBy({ idAccount: row.id_account })}
                    title={t("filter-title-user")}
                  />
                }
                rows={[
                  {
                    label: t("history-date"),
                    value: (
                      <FilterableValue
                        label={formatDate(row.date)}
                        active={
                          applied.dateFrom === row.date.slice(0, 10) &&
                          applied.dateTo === row.date.slice(0, 10)
                        }
                        onFilter={() => filterByDay(row.date)}
                        title={t("filter-title-day")}
                      />
                    ),
                  },
                  {
                    label: t("history-ip"),
                    value:
                      row.ip.trim() === "" ? (
                        "-"
                      ) : (
                        <FilterableValue
                          label={row.ip}
                          active={applied.ip === row.ip}
                          onFilter={() => filterBy({ ip: row.ip })}
                          title={t("filter-title-ip")}
                        />
                      ),
                  },
                  {
                    label: t("history-browser"),
                    value: (
                      <>
                        {row.browser.trim() === "" ? (
                          "-"
                        ) : (
                          <FilterableValue
                            label={row.browser}
                            active={applied.browser === row.browser}
                            onFilter={() => filterBy({ browser: row.browser })}
                            title={t("filter-title-browser")}
                          />
                        )}
                        {row.os.trim() !== "" && (
                          <span className="block text-xs text-grey-readable">
                            <FilterableValue
                              label={row.os}
                              active={applied.os === row.os}
                              onFilter={() => filterBy({ os: row.os })}
                              title={t("filter-title-os")}
                            />
                          </span>
                        )}
                      </>
                    ),
                  },
                  {
                    label: t("history-device-category"),
                    value: (
                      <FilterableValue
                        label={t(enumdevicecategoryToI18nKey[row.device_type])}
                        active={applied.deviceType === row.device_type}
                        onFilter={() =>
                          filterBy({ deviceType: row.device_type })
                        }
                        title={t("filter-title-device-category")}
                      />
                    ),
                  },
                ]}
              />
            ))}
          </div>

          {/* DESKTOP : le tableau complet. Le User-Agent brut n'est JAMAIS affiché —
              il est en base pour l'investigation, pas pour la lecture courante. */}
          <div className="hidden md:block mt-6 overflow-x-auto">
            <DataTable columns={columns} scrollKey={scrollKey}>
              {rows.map((row) => (
                <DataTableRow
                  key={row.id}
                  columns={columns}
                  cells={{
                    date: (
                      <>
                        {/* Clic = ce JOUR ; Maj+clic = étendre la plage jusqu'à ce jour.
                          Le clic simple REMPLACE toujours : s'il étendait tout seul,
                          on ne pourrait plus prévoir ce qu'un clic va donner. */}
                        <FilterableValue
                          label={formatDate(row.date)}
                          active={
                            applied.dateFrom === row.date.slice(0, 10) &&
                            applied.dateTo === row.date.slice(0, 10)
                          }
                          onFilter={() => filterByDay(row.date)}
                          onRangeSelect={() => extendToDay(row.date)}
                          title={t("filter-title-day-range")}
                        />
                        {/* La durée est une donnée de diagnostic (un login lent signale
                          un souci AUTH/USERS) : en second, pas en colonne. */}
                        <span className="block text-xs text-grey-readable">
                          {row.duration_ms} {t("history-duration-unit")}
                        </span>
                      </>
                    ),
                    email: (
                      <>
                        <span className="text-default-text break-all">
                          <FilterableValue
                            label={orDash(row.email)}
                            active={applied.idAccount === row.id_account}
                            onFilter={() =>
                              filterBy({ idAccount: row.id_account })
                            }
                            title={t("filter-title-user")}
                          />
                        </span>
                        {/* `account_type` est typé comme EnumUserType par le générateur,
                          mais vaut bien '' en base sur un ÉCHEC (le profil n'a jamais
                          été résolu). Le type ne peut pas l'exprimer — d'où ce test sur
                          la valeur réelle plutôt que sur le type. */}
                        {String(row.account_type).trim() !== "" && (
                          <span className="block text-xs text-grey-readable">
                            {row.account_type}
                          </span>
                        )}
                      </>
                    ),
                    ip: (
                      <>
                        {row.ip.trim() === "" ? (
                          "-"
                        ) : (
                          <FilterableValue
                            label={row.ip}
                            active={applied.ip === row.ip}
                            onFilter={() => filterBy({ ip: row.ip })}
                            title={t("filter-title-ip")}
                          />
                        )}
                      </>
                    ),
                    browser: (
                      <>
                        <span className="text-default-text">
                          {row.browser.trim() === "" ? (
                            "-"
                          ) : (
                            <FilterableValue
                              label={row.browser}
                              active={applied.browser === row.browser}
                              onFilter={() =>
                                filterBy({ browser: row.browser })
                              }
                              title={t("filter-title-browser")}
                            />
                          )}
                        </span>
                        <span className="block text-xs text-grey-readable">
                          {row.os.trim() === "" ? (
                            "-"
                          ) : (
                            <FilterableValue
                              label={row.os}
                              active={applied.os === row.os}
                              onFilter={() => filterBy({ os: row.os })}
                              title={t("filter-title-os")}
                            />
                          )}
                        </span>
                      </>
                    ),
                    category: (
                      <FilterableValue
                        label={t(enumdevicecategoryToI18nKey[row.device_type])}
                        active={applied.deviceType === row.device_type}
                        onFilter={() =>
                          filterBy({ deviceType: row.device_type })
                        }
                        title={t("filter-title-device-category")}
                      />
                    ),
                    status: (
                      <FilterableValue
                        variant="badge"
                        label={<LoginStatusBadge status={row.status} />}
                        active={applied.status === row.status}
                        onFilter={() => filterBy({ status: row.status })}
                        title={t("filter-title-status")}
                      />
                    ),
                  }}
                />
              ))}
            </DataTable>
          </div>

          <Pagination
            page={page}
            pageSize={LOGIN_HISTORY_PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
};
